import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, Observable, of, Subscription, switchMap } from 'rxjs';
import { TokenService } from './token.service';
import { environment } from 'src/environments/environment';
import { AuthToken } from '../models/auth-token';
import { SnackBarService } from './snack-bar.service';
import { NOTIFICATION_2_SOUND_BASE64, NOTIFICATION_SOUND_BASE64 } from '../constants/audio-base64';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private subscription?: Subscription;
  private tokenCheckInterval?: any;
  private expirationWarningShown = false;
  private readonly CHECK_AUTH_URL =
    environment.authUrl + '/api/Login/CheckAuth';
  private readonly TOKEN_REFRESH = environment.authUrl + '/api/Login/Refresh';
  private readonly LOGOUT_URL = environment.authUrl + '/api/Login/Logout';

  public static canTokenActivate(
    token?: AuthToken,
    authLevel?: readonly string[]
  ): boolean {
    if (!authLevel) {
      return true;
    }

    return authLevel.some((role) => token?.roles.includes(role));
  }

  constructor(
    private httpClient: HttpClient,
    private tokenService: TokenService,
    private snackBarService: SnackBarService
  ) {
    this.startTokenExpirationCheck();
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.stopTokenExpirationCheck();
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private startTokenExpirationCheck(): void {
    this.tokenCheckInterval = setInterval(() => {
      this.checkTokenExpiration();
    }, 3000);
  }

  private stopTokenExpirationCheck(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = undefined;
    }
  }

  private checkTokenExpiration(): void {
    this.tokenService.getTokenUserCredentials().pipe(
      switchMap((token) => {
        if (!token) {
          this.expirationWarningShown = false;
          this.logout();
          return of(null);
        }
        return this.tokenService.decodeToken(token);
      })
    ).subscribe((payload) => {
      if (!payload) {
        return;
      }

      const isExpiringSoon = payload.expiresAt
        ? payload.expiresAt.toDate().getTime() - Date.now() <= 20 * 60 * 1000
        : false;

      if (isExpiringSoon && !this.expirationWarningShown) {
        this.expirationWarningShown = true;
        this.playNotification();
        this.snackBarService.exibirAlerta(
          'Sua sessão expirará em breve. Realize alguma ação para continuar conectado.',
          'OK',
          {
            duration: 10000,
            verticalPosition: 'top',
            horizontalPosition: 'right',
            panelClass: ['snackbar', 'yellow-snackbar']
          }
        );
      }

      if (!isExpiringSoon && this.expirationWarningShown) {
        this.expirationWarningShown = false;
      }
    });
  }

  public playNotification(): void {
    const snd = new Audio(`data:audio/wav;base64,${NOTIFICATION_2_SOUND_BASE64}`);
    snd.play();
  }

  public async logout() {
    await this.httpClient
      .delete(this.LOGOUT_URL)
      .pipe(
        catchError((error) => {
          this.tokenService.clearTokenUserCredentials();
          if (error.status === 401) {
            location.href = `${environment.authUrl}/?url=${encodeURIComponent(location.host)}${encodeURIComponent(location.pathname)}`;
          }
          return of(null);
        })
      )
      .subscribe(() => {
        this.tokenService.clearTokenUserCredentials();
        location.href = `${environment.authUrl}/?url=${encodeURIComponent(location.host)}${encodeURIComponent(location.pathname)}`;
      });
  }

  public getToken(): Observable<string | null> {
    return this.tokenService.getTokenUserCredentials().pipe(
      switchMap((token) => {
        return of(token);
      })
    );
  }

  public refreshToken(): Observable<boolean> {
    return this.httpClient
      .get<boolean>(this.TOKEN_REFRESH)
      .pipe(
        switchMap(() => {
          return this.tokenService.getTokenUserCredentials().pipe(
            switchMap((newToken) => {
              if (newToken) {
                    return of(true);
              } else {
                console.error('❌ Refresh chamado mas cookie não foi atualizado!');
                return of(false);
              }
            })
          );
        }),
        catchError((error) => {
          console.error('❌ Erro ao chamar refresh:', error);
          return of(false);
        })
      );
  }
}
